import React, { useState } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Link } from 'react-router-dom';
import {
  BookOpen, LayoutDashboard, FileText, MessageSquare, Users,
  GraduationCap, Bell, CreditCard, Monitor, Award, Calendar,
  FolderOpen, Layers, Building2, UserCog, Megaphone, Bot,
  ChevronRight, ChevronDown, CheckCircle2, Circle, Sparkles,
  HelpCircle, ArrowRight, BookMarked
} from 'lucide-react';

const GUIDES = {
  student: {
    title: "Guide Etudiant",
    subtitle: "Decouvrez toutes les fonctionnalites de votre espace etudiant",
    sections: [
      {
        id: 'start',
        title: "Premiers pas",
        icon: Sparkles,
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        steps: [
          { text: "Connectez-vous avec vos identifiants", path: null },
          { text: "Consultez votre tableau de bord pour un apercu general", path: '/dashboard' },
          { text: "Verifiez votre profil et informations personnelles", path: '/profile' },
        ]
      },
      {
        id: 'academic',
        title: "Academique",
        icon: GraduationCap,
        color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        steps: [
          { text: "Consultez vos cours inscrits et materiels pedagogiques", path: '/courses' },
          { text: "Rejoignez la salle virtuelle Google Meet de votre promotion", path: '/online-courses' },
          { text: "Soumettez vos devoirs et travaux pratiques", path: '/assignments' },
          { text: "Consultez vos notes (systeme LMD, notation /20)", path: '/grades' },
          { text: "Verifiez votre emploi du temps et calendrier academique", path: '/schedule' },
          { text: "Suivez les deliberations et resultats officiels", path: '/deliberations' },
        ]
      },
      {
        id: 'class',
        title: "Ma Classe",
        icon: Users,
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        steps: [
          { text: "Voyez votre groupe de classe et camarades", path: '/my-class' },
          { text: "Rejoignez un groupe d'etude pour collaborer", path: '/study-groups' },
          { text: "Envoyez des messages texte et audio dans vos groupes", path: '/study-groups' },
        ]
      },
      {
        id: 'docs',
        title: "Documents",
        icon: FolderOpen,
        color: 'bg-amber-50 text-amber-700 border-amber-200',
        steps: [
          { text: "Demandez un document administratif (releve, attestation, etc.)", path: '/my-documents' },
          { text: "Suivez le statut de vos demandes de documents", path: '/my-documents' },
        ]
      },
      {
        id: 'communication',
        title: "Communication",
        icon: MessageSquare,
        color: 'bg-pink-50 text-pink-700 border-pink-200',
        steps: [
          { text: "Lisez les annonces de votre universite et classe", path: '/announcements' },
          { text: "Envoyez des messages a vos enseignants", path: '/messages' },
          { text: "Verifiez vos notifications en temps reel", path: '/notifications' },
        ]
      },
      {
        id: 'tools',
        title: "Outils",
        icon: Bot,
        color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        steps: [
          { text: "Posez vos questions a JosAide, l'assistant IA", path: '/ai-assistant' },
          { text: "Consultez la bibliotheque numerique", path: '/library' },
          { text: "Utilisez le dictionnaire integre", path: '/dictionary' },
        ]
      },
    ]
  },
  teacher: {
    title: "Guide Enseignant",
    subtitle: "Gerez vos cours, notes et etudiants efficacement",
    sections: [
      {
        id: 'start',
        title: "Premiers pas",
        icon: Sparkles,
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        steps: [
          { text: "Consultez votre tableau de bord avec statistiques", path: '/dashboard' },
          { text: "Completez votre profil enseignant", path: '/teacher-profile' },
        ]
      },
      {
        id: 'teaching',
        title: "Enseignement",
        icon: BookOpen,
        color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        steps: [
          { text: "Gerez vos cours et ajoutez des ressources pedagogiques", path: '/courses' },
          { text: "Accedez a la salle virtuelle Google Meet pour vos promotions", path: '/online-courses' },
          { text: "Creez et gerez les devoirs de vos etudiants", path: '/assignments' },
          { text: "Saisissez les notes de vos etudiants (notation /20 LMD)", path: '/grades' },
        ]
      },
      {
        id: 'communication',
        title: "Communication",
        icon: Megaphone,
        color: 'bg-pink-50 text-pink-700 border-pink-200',
        steps: [
          { text: "Publiez des annonces pour vos etudiants", path: '/announcements' },
          { text: "Envoyez des messages internes", path: '/messages' },
          { text: "Gerez les groupes d'etude de vos promotions", path: '/study-groups' },
        ]
      },
      {
        id: 'tools',
        title: "Outils",
        icon: Bot,
        color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        steps: [
          { text: "Utilisez JosAide pour naviguer dans l'application", path: '/ai-assistant' },
          { text: "Consultez les analyses et statistiques", path: '/analytics' },
        ]
      },
    ]
  },
  admin: {
    title: "Guide Administrateur",
    subtitle: "Configurez et gerez votre institution universitaire",
    sections: [
      {
        id: 'start',
        title: "Premiers pas",
        icon: Sparkles,
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        steps: [
          { text: "Consultez le tableau de bord global de l'institution", path: '/dashboard' },
          { text: "Verifiez la structure organisationnelle", path: '/organization' },
        ]
      },
      {
        id: 'structure',
        title: "Structure Academique",
        icon: Building2,
        color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        steps: [
          { text: "Gerez les facultes de votre institution", path: '/faculties' },
          { text: "Configurez les departements et programmes LMD", path: '/departments' },
          { text: "Creez et gerez les promotions (L1, L2, L3, M1, M2)", path: '/programs' },
          { text: "Gerez les groupes de classe", path: '/class-groups' },
        ]
      },
      {
        id: 'online',
        title: "Cours en Ligne",
        icon: Monitor,
        color: 'bg-cyan-50 text-cyan-700 border-cyan-200',
        steps: [
          { text: "Configurez le lien Google Meet par promotion dans Cours en Ligne", path: '/online-courses' },
          { text: "Un seul lien par promotion (ex: L1, L2), partage a tous les etudiants", path: '/online-courses' },
        ]
      },
      {
        id: 'users',
        title: "Utilisateurs",
        icon: Users,
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        steps: [
          { text: "Gerez les comptes utilisateurs (etudiants, enseignants, personnel)", path: '/users' },
          { text: "Configurez les roles et permissions (28+ roles RBAC)", path: '/roles' },
          { text: "Validez les demandes d'inscription", path: '/registrations' },
        ]
      },
      {
        id: 'academic',
        title: "Gestion Academique",
        icon: GraduationCap,
        color: 'bg-amber-50 text-amber-700 border-amber-200',
        steps: [
          { text: "Configurez l'annee academique et les periodes", path: '/academic-year' },
          { text: "Gerez les cours et affectations enseignants", path: '/courses' },
          { text: "Suivez les deliberations", path: '/deliberations' },
        ]
      },
      {
        id: 'communication',
        title: "Communication",
        icon: Megaphone,
        color: 'bg-pink-50 text-pink-700 border-pink-200',
        steps: [
          { text: "Publiez des annonces institutionnelles", path: '/announcements' },
          { text: "Verifiez les notifications et messages", path: '/notifications' },
        ]
      },
      {
        id: 'tools',
        title: "Outils & Systeme",
        icon: UserCog,
        color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        steps: [
          { text: "Consultez les analyses et statistiques", path: '/analytics' },
          { text: "Gerez les paiements et finances", path: '/payments' },
          { text: "Utilisez JosAide pour naviguer", path: '/ai-assistant' },
        ]
      },
    ]
  },
  super_admin: {
    title: "Guide Super Admin",
    subtitle: "Gerez l'ensemble de la plateforme JoSmart Uni",
    sections: [
      {
        id: 'start',
        title: "Premiers pas",
        icon: Sparkles,
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        steps: [
          { text: "Consultez le tableau de bord global de la plateforme", path: '/dashboard' },
          { text: "Gerez toutes les universites sur la plateforme", path: '/universities' },
        ]
      },
      {
        id: 'management',
        title: "Gestion de la Plateforme",
        icon: Building2,
        color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        steps: [
          { text: "Deleguez la gestion a des co-administrateurs", path: '/co-admins' },
          { text: "Consultez la boite de reception des demandes de collaboration", path: '/collaboration-inbox' },
          { text: "Gerez les agents de support par province", path: '/support' },
        ]
      },
      {
        id: 'analytics',
        title: "Analyses",
        icon: Award,
        color: 'bg-amber-50 text-amber-700 border-amber-200',
        steps: [
          { text: "Consultez les statistiques globales", path: '/analytics' },
          { text: "Parrainez de nouvelles institutions", path: '/referral' },
        ]
      },
    ]
  }
};

const getRoleGuide = (role) => {
  if (role === 'super_admin') return GUIDES.super_admin;
  if (role === 'student') return GUIDES.student;
  if (['teacher', 'visiting_teacher'].includes(role)) return GUIDES.teacher;
  // All other admin/staff roles
  return GUIDES.admin;
};

export const GuidePage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [completedSteps, setCompletedSteps] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`guide_progress_${user?.id}`) || '[]');
    } catch { return []; }
  });
  const [expandedSection, setExpandedSection] = useState(null);
  const fr = language === 'fr';

  const guide = getRoleGuide(user?.role);
  if (!guide) return null;

  const toggleStep = (stepId) => {
    const updated = completedSteps.includes(stepId)
      ? completedSteps.filter(s => s !== stepId)
      : [...completedSteps, stepId];
    setCompletedSteps(updated);
    localStorage.setItem(`guide_progress_${user?.id}`, JSON.stringify(updated));
  };

  const totalSteps = guide.sections.reduce((acc, s) => acc + s.steps.length, 0);
  const completedCount = completedSteps.length;
  const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  return (
    <div className="space-y-6 animate-slide-in" data-testid="guide-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-testid="guide-title">
          {guide.title}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{guide.subtitle}</p>
      </div>

      {/* Progress */}
      <Card className="border-0 bg-gradient-to-r from-[#0F2C59] to-[#1a3f7a] text-white" data-testid="guide-progress">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BookMarked className="w-5 h-5" />
              <span className="font-semibold">{fr ? 'Votre progression' : 'Your progress'}</span>
            </div>
            <Badge className="bg-white/20 text-white border-0 text-sm">
              {completedCount}/{totalSteps} {fr ? 'etapes' : 'steps'}
            </Badge>
          </div>
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-sm text-blue-200 mt-2">
            {progressPercent === 100
              ? (fr ? 'Felicitations ! Vous maitrisez JoSmart Uni !' : 'Congratulations! You mastered JoSmart Uni!')
              : (fr ? `${progressPercent}% complete - continuez a explorer !` : `${progressPercent}% complete - keep exploring!`)}
          </p>
        </CardContent>
      </Card>

      {/* Guide Sections */}
      <div className="space-y-3">
        {guide.sections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSection === section.id;
          const sectionCompleted = section.steps.every((_, idx) =>
            completedSteps.includes(`${section.id}-${idx}`)
          );
          const sectionProgress = section.steps.filter((_, idx) =>
            completedSteps.includes(`${section.id}-${idx}`)
          ).length;

          return (
            <Card key={section.id} className="overflow-hidden" data-testid={`guide-section-${section.id}`}>
              <button
                className="w-full p-4 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-left"
                onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                data-testid={`toggle-section-${section.id}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center border flex-shrink-0 ${section.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{section.title}</h3>
                    {sectionCompleted && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                  </div>
                  <p className="text-xs text-gray-400">
                    {sectionProgress}/{section.steps.length} {fr ? 'etapes' : 'steps'}
                  </p>
                </div>
                {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-2 border-t border-gray-100 dark:border-slate-700 pt-3">
                  {section.steps.map((step, idx) => {
                    const stepId = `${section.id}-${idx}`;
                    const isDone = completedSteps.includes(stepId);
                    return (
                      <div
                        key={idx}
                        className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                          isDone ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700'
                        }`}
                        data-testid={`guide-step-${stepId}`}
                      >
                        <button
                          onClick={() => toggleStep(stepId)}
                          className="flex-shrink-0"
                          data-testid={`check-step-${stepId}`}
                        >
                          {isDone
                            ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                            : <Circle className="w-5 h-5 text-gray-300" />
                          }
                        </button>
                        <span className={`text-sm flex-1 ${isDone ? 'text-green-700 dark:text-green-400 line-through' : 'text-gray-700 dark:text-gray-300'}`}>
                          {step.text}
                        </span>
                        {step.path && (
                          <Link to={step.path}>
                            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-[var(--tenant-primary)]" data-testid={`go-to-${step.path.replace('/', '')}`}>
                              <ArrowRight className="w-3 h-3" />
                              {fr ? 'Aller' : 'Go'}
                            </Button>
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Help Card */}
      <Card className="bg-gray-50 dark:bg-slate-800 border-dashed" data-testid="guide-help-card">
        <CardContent className="p-5 text-center">
          <HelpCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            {fr ? "Besoin d'aide supplementaire ?" : "Need more help?"}
          </p>
          <div className="flex justify-center gap-3">
            <Link to="/ai-assistant">
              <Button variant="outline" size="sm" className="gap-1" data-testid="guide-ai-link">
                <Bot className="w-3.5 h-3.5" />
                JosAide
              </Button>
            </Link>
            <Link to="/support">
              <Button variant="outline" size="sm" className="gap-1" data-testid="guide-support-link">
                <HelpCircle className="w-3.5 h-3.5" />
                Support
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GuidePage;
